// Levá lišta (rail) podle prototypu: skupiny = kategorie z GET /ja (pořadí serveru), položky
// s piktogramem a hvězdičkou ★ (oblíbené = Rychlé přístupy), sbalitelné skupiny, šířka tažením
// v mezích tokenů (170–420 px, dvojklik vrátí 236), sbalený režim jen s ikonami (58 px).
// Skupina s aktivní položkou je vždy otevřená. Sekce bez kategorie (asistent…) jsou za skupinami.
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from 'react';
import { Link } from 'react-router';
import {
  LISTA_SIRKA_MAX,
  LISTA_SIRKA_MIN,
  LISTA_SIRKA_VYCHOZI,
  omezitSirkuListy,
} from '../api/nastaveni';
import { useIkonyMenu } from '../api/menu-lista';
import { IKONA_SEKCE, IKONY, Ikona, IkonaSekce } from './ikony';
import type { Menu, PolozkaMenu, SkupinaMenu } from './menu';
import { vsechnyPolozky } from './menu';
import { useNastaveni } from './nastaveni-uzivatele';
import { PocetKVyrizeni } from './PocetKVyrizeni';
import { PocetSvozu } from './PocetSvozu';
import { DOTAZ_VYSOUVACI_MENU, useMediaDotaz } from './telefon';
import './Shell.css';
import { lina, NacitaniCasti } from '../komponenty/NacitaniCasti';

// Počet „Ke kontrole“ čte čtyři fronty (api/ke-kontrole.ts) — kód se stáhne až po startu; odznak se ukazuje až
// s načtenými počty, takže se nic neposune (rozpočet hlavního balíku).
const PocetKeKontrole = lina<{ testId?: string }>(() =>
  import('./PocetKeKontrole').then((m) => m.PocetKeKontrole),
);

export const KLIC_SKUPINY_OBLIBENE = 'oblibene';

interface Props {
  menu: Menu;
  /** Klíč aktivní sekce (z cesty). */
  aktivniKlic?: string;
  /** Vysunutá přes obsah (pod prahem šířky okna). */
  otevrena?: boolean;
  /** Zavřít vysunutou lištu (klik na položku nebo mimo). */
  zavrit?: () => void;
}

function PolozkaListy({
  polozka,
  aktivni,
  veSkupine,
  oblibena,
  sbalena,
  naOblibenou,
  naKlik,
  ikonaId,
}: {
  /** Piktogram zvolený administrátorem (Nastavení › Menu); bez něj výchozí podle sekce. */
  ikonaId?: string;
  polozka: PolozkaMenu;
  aktivni: boolean;
  veSkupine: boolean;
  oblibena: boolean;
  sbalena: boolean;
  naOblibenou: (klic: string) => void;
  naKlik?: () => void;
}) {
  // Záložní písmeno podle záznamu v `IKONA_SEKCE`, ne podle prvku JSX (ten je vždy definovaný — SKL-S2, DIS-S6).
  const zvolena = ikonaId && IKONY[ikonaId] ? ikonaId : undefined;
  const ikona = zvolena ? (
    <Ikona id={zvolena} velikost={15} />
  ) : IKONA_SEKCE[polozka.klic] ? (
    <IkonaSekce klic={polozka.klic} />
  ) : null;
  return (
    <li
      className={`lista__radek${aktivni ? ' lista__radek--aktivni' : ''}${veSkupine ? ' lista__radek--ve-skupine' : ''}`}
      data-sekce={polozka.klic}
    >
      <Link
        to={polozka.cesta}
        className="lista__polozka"
        aria-current={aktivni ? 'page' : undefined}
        title={sbalena ? polozka.nazev : undefined}
        aria-label={sbalena ? polozka.nazev : undefined}
        onClick={naKlik}
      >
        <span className="lista__ikona" aria-hidden="true">
          {ikona ?? polozka.nazev.charAt(0).toUpperCase()}
        </span>
        <span className="lista__nazev">{polozka.nazev}</span>
        {polozka.klic === 'ke_kontrole' && !sbalena && (
          <NacitaniCasti tiche>
            <PocetKeKontrole />
          </NacitaniCasti>
        )}
        {polozka.klic === 'k_vyrizeni' && !sbalena && <PocetKVyrizeni />}
        {polozka.klic === 'svoz_pozadavky' && !sbalena && <PocetSvozu />}
      </Link>
      {!sbalena && polozka.klic !== 'home' && (
        <button
          type="button"
          className="lista__hvezda"
          aria-pressed={oblibena}
          aria-label={
            oblibena
              ? `Odebrat ${polozka.nazev} z oblíbených`
              : `Přidat ${polozka.nazev} do oblíbených`
          }
          title={
            oblibena ? 'Odebrat z oblíbených' : 'Přidat do oblíbených (nahoře v menu a na Domů)'
          }
          onClick={() => naOblibenou(polozka.klic)}
        >
          <Ikona id="star" velikost={13} vyplnena={oblibena} />
        </button>
      )}
    </li>
  );
}

function SkupinaListy({
  skupina,
  otevrena,
  oblibene,
  naPrepnuti,
  vykreslitPolozku,
}: {
  skupina: SkupinaMenu;
  otevrena: boolean;
  oblibene?: boolean;
  naPrepnuti: () => void;
  vykreslitPolozku: (polozka: PolozkaMenu) => JSX.Element;
}) {
  const idSeznamu = `lista-skupina-${skupina.klic.replace(/[^\w-]/g, '_')}`;
  return (
    <li data-skupina={skupina.klic}>
      <button
        type="button"
        className={`lista__skupina${oblibene ? ' lista__skupina--oblibene' : ''}`}
        aria-expanded={otevrena}
        aria-controls={otevrena ? idSeznamu : undefined}
        onClick={naPrepnuti}
      >
        <span className="lista__skupina-sipka" aria-hidden="true">
          <Ikona id="chevron_down" velikost={12} />
        </span>
        {oblibene && <Ikona id="star" velikost={11} vyplnena />}
        <span className="lista__skupina-nazev">{skupina.nazev}</span>
      </button>
      {otevrena && (
        <ul id={idSeznamu} className="lista__seznam">
          {skupina.polozky.map(vykreslitPolozku)}
        </ul>
      )}
    </li>
  );
}

export function Lista({ menu, aktivniKlic, otevrena = false, zavrit }: Props) {
  const { nastaveni, upravit, prepnoutOblibenou, prepnoutSkupinu } = useNastaveni();
  const ikonyMenu = useIkonyMenu();
  // Sbalení na ikony je volba pro lištu vlevo. Pod 950 px je lišta vysouvací přes obsah (tablet na výšku, telefon)
  // a sbalená by v ní zůstaly jen ikony bez názvů — tam se proto vždy ukazuje celá (krok 13.7a).
  const vysouvaci = useMediaDotaz(DOTAZ_VYSOUVACI_MENU);
  const sbalena = nastaveni.rail_collapsed && !vysouvaci;
  // Během tažení se šířka drží lokálně (plynulé), uloží se až po puštění.
  const [tazenaSirka, setTazenaSirka] = useState<number | null>(null);
  const tazeni = useRef<{ start: number; puvodni: number } | null>(null);
  const sirka = tazenaSirka ?? nastaveni.rail_width;

  const ulozitSirku = useCallback(
    (nova: number) => {
      setTazenaSirka(null);
      upravit({ rail_width: omezitSirkuListy(nova) });
    },
    [upravit],
  );

  useEffect(() => {
    if (!tazeni.current) return undefined;
    const naPohyb = (udalost: globalThis.PointerEvent) => {
      const t = tazeni.current;
      if (!t) return;
      setTazenaSirka(omezitSirkuListy(t.puvodni + (udalost.clientX - t.start)));
    };
    const naPusteni = (udalost: globalThis.PointerEvent) => {
      const t = tazeni.current;
      tazeni.current = null;
      if (t) ulozitSirku(t.puvodni + (udalost.clientX - t.start));
    };
    window.addEventListener('pointermove', naPohyb);
    window.addEventListener('pointerup', naPusteni);
    window.addEventListener('pointercancel', naPusteni);
    return () => {
      window.removeEventListener('pointermove', naPohyb);
      window.removeEventListener('pointerup', naPusteni);
      window.removeEventListener('pointercancel', naPusteni);
    };
  }, [tazenaSirka, ulozitSirku]);

  const naZacatekTazeni = (udalost: PointerEvent<HTMLDivElement>) => {
    if (sbalena) return;
    udalost.preventDefault();
    tazeni.current = { start: udalost.clientX, puvodni: nastaveni.rail_width };
    setTazenaSirka(nastaveni.rail_width);
  };

  const naKlavesuTahla = (udalost: KeyboardEvent<HTMLDivElement>) => {
    const krok = 10;
    let nova: number | null = null;
    if (udalost.key === 'ArrowLeft') nova = nastaveni.rail_width - krok;
    else if (udalost.key === 'ArrowRight') nova = nastaveni.rail_width + krok;
    else if (udalost.key === 'Home') nova = LISTA_SIRKA_MIN;
    else if (udalost.key === 'End') nova = LISTA_SIRKA_MAX;
    if (nova !== null) {
      udalost.preventDefault();
      ulozitSirku(nova);
    }
  };

  const oblibene = nastaveni.quick_links
    .map((klic) => vsechnyPolozky(menu).find((p) => p.klic === klic))
    .filter((p): p is PolozkaMenu => Boolean(p) && p?.klic !== 'home');
  const zavrene = new Set(nastaveni.rail_groups_closed);

  const vykreslitPolozku = (polozka: PolozkaMenu, veSkupine: boolean) => (
    <PolozkaListy
      key={polozka.klic}
      polozka={polozka}
      aktivni={polozka.klic === aktivniKlic}
      veSkupine={veSkupine && !sbalena}
      oblibena={nastaveni.quick_links.includes(polozka.klic)}
      sbalena={sbalena}
      naOblibenou={prepnoutOblibenou}
      naKlik={zavrit}
      ikonaId={ikonyMenu[polozka.klic]}
    />
  );

  const skupinaOblibene: SkupinaMenu = {
    klic: KLIC_SKUPINY_OBLIBENE,
    nazev: 'Oblíbené',
    polozky: oblibene,
  };

  return (
    <>
      {otevrena && (
        <button
          type="button"
          className="prekryv lista__podklad"
          aria-label="Zavřít menu"
          onClick={zavrit}
        />
      )}
      <aside
        className={`lista${sbalena ? ' lista--sbalena' : ''}`}
        style={{ '--lista-sirka': `${sbalena ? 58 : sirka}px` } as React.CSSProperties}
        data-testid="lista"
        data-sbalena={sbalena ? 'ano' : 'ne'}
      >
        {!sbalena && (
          <div
            className="lista__tahlo"
            role="separator"
            aria-orientation="vertical"
            aria-label="Šířka menu"
            aria-valuemin={LISTA_SIRKA_MIN}
            aria-valuemax={LISTA_SIRKA_MAX}
            aria-valuenow={sirka}
            tabIndex={0}
            title="Tažením změníte šířku menu, dvojklik vrátí výchozí"
            onPointerDown={naZacatekTazeni}
            onDoubleClick={() => ulozitSirku(LISTA_SIRKA_VYCHOZI)}
            onKeyDown={naKlavesuTahla}
          />
        )}
        <nav className="lista__obsah" aria-label="Hlavní menu">
          <ul className="lista__seznam">
            {/* Domů v liště není (stejně jako v prototypu) — vede na něj logo v hlavičce.
                Jako první položka by jeho zelené zvýraznění tvořilo zub pod zelenou hlavičkou. */}
            {oblibene.length > 0 && !sbalena && (
              <SkupinaListy
                skupina={skupinaOblibene}
                otevrena={
                  !zavrene.has(KLIC_SKUPINY_OBLIBENE) ||
                  oblibene.some((p) => p.klic === aktivniKlic)
                }
                oblibene
                naPrepnuti={() => prepnoutSkupinu(KLIC_SKUPINY_OBLIBENE)}
                vykreslitPolozku={(p) => vykreslitPolozku(p, true)}
              />
            )}
            {oblibene.length > 0 && sbalena && (
              <>
                {oblibene.map((p) => vykreslitPolozku(p, false))}
                <li className="lista__oddelovac" aria-hidden="true" />
              </>
            )}
            {menu.skupiny.map((skupina) =>
              sbalena ? (
                skupina.polozky.map((p) => vykreslitPolozku(p, false))
              ) : (
                <SkupinaListy
                  key={skupina.klic}
                  skupina={skupina}
                  otevrena={
                    !zavrene.has(skupina.klic) ||
                    skupina.polozky.some((p) => p.klic === aktivniKlic)
                  }
                  naPrepnuti={() => prepnoutSkupinu(skupina.klic)}
                  vykreslitPolozku={(p) => vykreslitPolozku(p, true)}
                />
              ),
            )}
            {menu.ostatni.length > 0 && !sbalena && (
              <li className="lista__oddelovac" aria-hidden="true" />
            )}
            {menu.ostatni.map((p) => vykreslitPolozku(p, false))}
          </ul>
        </nav>
        <button
          type="button"
          className="lista__sbalit"
          title="Sbalit / rozbalit menu"
          aria-label={sbalena ? 'Rozbalit menu' : 'Sbalit menu'}
          aria-expanded={!sbalena}
          onClick={() => upravit({ rail_collapsed: !sbalena })}
        >
          <Ikona id={sbalena ? 'chevron_right' : 'chevron_left'} velikost={16} />
        </button>
      </aside>
    </>
  );
}
